import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import Table from "../../core/common/dataTable/index";
import api from "../../api";
import socket from "../../socket";
import nodeApi from "../../nodeApi";
import useDynamicPageSize from "../../useDynamicPageSize";
import Footer from "../../Footer";
import { toast, ToastContainer } from "react-toastify";
import { useHeaderFilters } from "../../context/HeaderFilterContext";
import "react-toastify/dist/ReactToastify.css";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap/dist/js/bootstrap.bundle.min.js";
import { DatePicker } from "antd";
import { Dayjs } from "dayjs";
import CommonSelect from "../../core/common/commonSelect";
import PredefinedDateRanges from "../../core/common/datePicker";
import dayjs from "dayjs";
import { useLocation, useNavigate } from "react-router-dom";
import { encryptQuery, decryptQuery } from "../../utils/encryption";
import { useIsMobile } from "../../hooks/useIsMobile";
import { REACT_APP_CAREER_API_BASE_URL } from "../../environment";

const DEFAULT_END_ISO = dayjs().format("YYYY-MM-DD");
const DEFAULT_START_ISO = dayjs().subtract(29, "day").format("YYYY-MM-DD");
const DEFAULT_START_LABEL = dayjs(DEFAULT_START_ISO).format("DD-MMM-YYYY");
const DEFAULT_END_LABEL = dayjs(DEFAULT_END_ISO).format("DD-MMM-YYYY");

type Option = { label: string; value: any };
type CandidateDetails = any;
type ResumeRow = {
  id: number;
  name: string;
  email: string;
  contact: string;
  total_experience_months: number;
  designation: string;
  companies_worked: number;
  last_company: string;
  category_name: string;
};
type ResumeFilters = {
  search: string;
  category_id: string | number | "";
  source?: string;
  date_from?: string; // YYYY-MM-DD
  date_to?: string;   // YYYY-MM-DD
  currentPage?: number;
};
type ResumesFiltersProps = {
  searchTerm: string;
  onSearchTermChange: (v: string) => void;
  categoryOptions: Option[];
  selectedCategory: any;
  onCategoryChange: (opt: Option | null) => void;
  startLabel: string;
  endLabel: string;
  onDateRangeChange: (s: string, e: string) => void;
  onResetFilters: () => void;
  showReset: boolean;
  sourceOptions: Option[];
  selectedSource: string;
  onSourceChange: (opt: Option | null) => void;
  onDatePickerClick?: () => void;
  onPlusClick?: () => void;
  oldestAppliedISO?: string | null;
  filtersLoading?: any;
};

type DialOption = { label: string; value: string };
const ResumesFilters: React.FC<ResumesFiltersProps> = ({
  searchTerm,
  onSearchTermChange,
  categoryOptions,
  selectedCategory,
  onCategoryChange,
  startLabel,
  endLabel,
  onDateRangeChange,
  onResetFilters,
  showReset,
  sourceOptions,
  onSourceChange,
  selectedSource,
  onDatePickerClick,
  onPlusClick,
  oldestAppliedISO,
  filtersLoading
}) => {
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);
  const currentMonthStart = dayjs().startOf("month").format("YYYY-MM-DD");
  const currentMonthEnd = dayjs().endOf("month").format("YYYY-MM-DD");


  // inside ResumesFilters
  const dpRef = useRef<HTMLDivElement>(null);
  const [dpWidth, setDpWidth] = useState<number>();

  useLayoutEffect(() => {
    if (dpRef.current) {
      setDpWidth(dpRef.current.offsetWidth);
    }
  }, []);

  // close on outside click / ESC
  useEffect(() => {
    const clickOutside = (e: MouseEvent) => {
      if (showMobileFilters && modalRef.current && !modalRef.current.contains(e.target as Node)) {
        setShowMobileFilters(false);
      }
    };
    const onEsc = (e: KeyboardEvent) => e.key === "Escape" && setShowMobileFilters(false);
    document.addEventListener("mousedown", clickOutside);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", clickOutside);
      document.removeEventListener("keydown", onEsc);
    };
  }, [showMobileFilters]);


  const anyFilterOn = searchTerm.trim() || selectedCategory || startLabel || endLabel;


  return (
    <div className="upper-absolute-class">
      {/* Mobile */}
      <div className="d-lg-none">
        <div className="d-flex align-items-center gap-2" style={{ paddingRight: 12 }}>
          {showReset && (
            <button className="btn btn-outline-primary add_btn_size" onClick={onResetFilters} title="Reset">
              <i className="ti ti-refresh" />
            </button>
          )}

          <div className="d-flex w-100 gap-2">
            <input
              type="text"
              className="form-control search-size w-45"
              placeholder="Search…"
              value={searchTerm}
              onChange={(e) => onSearchTermChange(e.target.value)}
            />
          </div>
          {/* MOBILE datepicker (inside the d-lg-none block) */}
          <div ref={dpRef} className="input-icon-end position-relative custom-datepicker holiday-flex w-100 datepicker-anchor preset-popover" onClick={onDatePickerClick} >
            <div
              className="datepicker-shell"
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                minHeight: 38,
                borderRadius: 4,
                width: "100%",
              }}
            >
              {filtersLoading || !startLabel || !endLabel ? (
                //  Skeleton stays INSIDE the container
                <div
                  style={{
                    flex: 1,
                    height: 30,                // small bar like text
                    borderRadius: 4,
                    background: "linear-gradient(90deg,#eee 25%,#ddd 37%,#eee 63%)",
                    backgroundSize: "400% 100%",
                    animation: "skeleton-loading 1.4s ease infinite",
                  }}
                />
              ) : (
                <PredefinedDateRanges
                  key={`${startLabel}-${endLabel}`}
                  startDate={dayjs(startLabel, "DD-MMM-YYYY", true).format("YYYY-MM-DD")}
                  endDate={dayjs(endLabel, "DD-MMM-YYYY", true).format("YYYY-MM-DD")}
                  onDateRangeChange={(isoStart, isoEnd) =>
                    onDateRangeChange(
                      dayjs(isoStart).format("DD-MMM-YYYY"),
                      dayjs(isoEnd).format("DD-MMM-YYYY")
                    )
                  }
                  oldestAppliedISO={oldestAppliedISO}
                />
              )}
            </div>

            <span className="input-icon-addon">
              <i className="ti ti-chevron-down" />
            </span>
          </div>


          <button
            className="btn btn-outline-primary add_btn_size"
            type="button"
            onClick={() => onPlusClick?.()}
            title="Generate link / Upload resumes"

          >
            <i className="ti ti-circle-plus" />
          </button>


          <button className="btn btn-outline-primary add_btn_size" onClick={() => setShowMobileFilters(!showMobileFilters)}>
            <i className="ti ti-filter" />
          </button>
        </div>

        {showMobileFilters && (
          <div
            className="modal-backdrop"
            style={{
              position: "fixed",
              inset: 0,
              backgroundColor: "rgba(0,0,0,0.3)",
              display: "flex",
              justifyContent: "center",
              alignItems: "flex-start",
              paddingTop: "103px",
              zIndex: 1050,
            }}
          >
            <div
              ref={modalRef}
              className="bg-white shadow rounded p-3 mt-2 z-3 filter_select"
              style={{ width: "90%", maxWidth: 400 }}
            >
              <div className="d-flex flex-column gap-2">
                <CommonSelect
                  className="select"
                  forceFullWidth
                  options={[{ label: "All Categories", value: "" }, ...categoryOptions]}
                  value={categoryOptions.find((o) => o.value === selectedCategory) ?? { label: "All Categories", value: "" }}
                  onChange={(selected) => {
                    onCategoryChange(selected as Option);
                    setShowMobileFilters(false);
                  }}
                />
                <CommonSelect
                  className="select"
                  forceFullWidth
                  options={sourceOptions}
                  value={sourceOptions.find((o) => o.value === selectedSource) ?? sourceOptions[0]}
                  onChange={(selected) => {
                    onSourceChange(selected as Option);
                    setShowMobileFilters(false);
                  }}
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Desktop */}
      <div className="Employee-filters d-none d-lg-flex align-items-center flex-wrap gap-2 ">
        {showReset && (
          <button className="btn btn-outline-primary add_btn_size" onClick={onResetFilters} title="Reset">
            <i className="ti ti-refresh" />
          </button>
        )}
        <input
          type="text"
          className="form-control search-size"
          placeholder="Search…"
          value={searchTerm}
          onChange={(e) => onSearchTermChange(e.target.value)}
        />
        <CommonSelect
          className="select"
          options={[{ label: "All Categories", value: "" }, ...categoryOptions]}
          value={categoryOptions.find((o) => o.value === selectedCategory) ?? { label: "All Categories", value: "" }}
          onChange={onCategoryChange}
        />
        <CommonSelect
          className="select"
          options={sourceOptions}
          value={sourceOptions.find((o) => o.value === selectedSource) ?? sourceOptions[0]}
          onChange={onSourceChange}
        />
        {/* DESKTOP datepicker */}
        <div ref={dpRef} className="input-icon-end position-relative custom-datepicker" onClick={onDatePickerClick} style={{
          minWidth: 170,
          maxWidth: "fit-content",
          minHeight: 32,
          flexShrink: 0,
        }}>
          <div
            className="datepicker-shell"
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              minHeight: 38,
              borderRadius: 4,
              width: "100%",
            }}
          >
            {filtersLoading || !startLabel || !endLabel ? (
              //  Skeleton stays INSIDE the container
              <div
                style={{
                  flex: 1,
                  height: 30,                // small bar like text
                  borderRadius: 4,
                  background: "linear-gradient(90deg,#eee 25%,#ddd 37%,#eee 63%)",
                  backgroundSize: "400% 100%",
                  animation: "skeleton-loading 1.4s ease infinite",
                }}
              />
            ) : (
              <PredefinedDateRanges
                key={`${startLabel}-${endLabel}`}
                startDate={dayjs(startLabel, "DD-MMM-YYYY", true).format("YYYY-MM-DD")}
                endDate={dayjs(endLabel, "DD-MMM-YYYY", true).format("YYYY-MM-DD")}
                onDateRangeChange={(isoStart, isoEnd) =>
                  onDateRangeChange(
                    dayjs(isoStart).format("DD-MMM-YYYY"),
                    dayjs(isoEnd).format("DD-MMM-YYYY")
                  )
                }
                oldestAppliedISO={oldestAppliedISO}
              />
            )}
          </div>

          <span className="input-icon-addon">
            <i className="ti ti-chevron-down" />
          </span>
        </div>

        <button
          className="btn btn-outline-primary add_btn_size"
          style={{ marginLeft: "20px" }}
          type="button"
          onClick={() => onPlusClick?.()}
          title="Generate link / Upload resumes"
        >
          <i className="ti ti-circle-plus" />
        </button>




      </div>
    </div>
  );
};
const Resumes: React.FC = () => {
  const { setFilters } = useHeaderFilters();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile(991);
  const [oldestDate, setOldestDate] = useState<string | null>(null);
  // When true, the UI labels track the global oldest automatically.
  // It starts true (labels follow server), and flips to false if the user picks a custom start.
  const followGlobalOldestRef = useRef(true);
  const oldestFetchedRef = useRef(false);
  const [searchTerm, setSearchTerm] = useState("");
  const isInitialFetch = useRef(true);
  const userInitiatedSearchRef = useRef(false);
  const debounceTimeoutRef = useRef<any>(null);
  const [filtersReady, setFiltersReady] = useState(false);
  const navCausedRef = useRef(false);     // we navigated ourselves → skip re-hydration
  const hydratingRef = useRef(false);     // avoid flipping filtersReady unless truly hydrating
  const lastQueryRef = useRef<string>(""); // request de-duplication key
  const [editData, setEditData] = useState<any>(null);
  const [editOriginalName, setEditOriginalName] = useState<string>("");
  const [editSaving, setEditSaving] = useState(false);
  // Oldest "Date Applied" we’ve seen
  const [oldestAppliedISO, setOldestAppliedISO] = useState<string | null>(null);
  const resetCareerModalState = () => {
    setSelectedFiles([]);
    setUploadResp(null);
    setGenLinkData(null);
  };

  // Only initialize from oldest once (and never while hydrating from URL)
  const initializedFromOldestRef = useRef(false);
  const getMinAppliedDate = (rows: any[]) => {
    let min: string | null = null;
    for (const r of rows || []) {
      const iso = r?.date ? dayjs(r.date).format("YYYY-MM-DD") : null;
      if (iso && (!min || iso < min)) min = iso;
    }
    return min;
  };
  const MIN_CTC_LPA = 0.5;
  const MAX_CTC_LPA = 100;

  const isCTCInRange = (ctc: string) => {
    const nums = ctc.match(/\d+(\.\d{1,2})?/g);
    if (!nums) return true; // Not Available
    return nums.every(n => {
      const v = parseFloat(n);
      return v >= MIN_CTC_LPA && v <= MAX_CTC_LPA;
    });
  };

  const CTC_ALLOWED_CHARS = /^[0-9a-zA-Z\s:.,]*$/;
  const CTC_REGEX =
    /^(Not\s*Available|((Current|Expected)\s*:\s*\d+(\.\d{1,2})?\s*LPA)(\s*,\s*(Current|Expected)\s*:\s*\d+(\.\d{1,2})?\s*LPA)*|\d+(\.\d{1,2})?\s*LPA)$/i;
  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i;   // general/global email format
  const NO_DIGITS_OPTIONAL_RE = /^[^0-9]*$/;           // allow anything except numbers (empty allowed)
  const NO_DIGITS_REQUIRED_RE = /^(?!\s*$)[^0-9]*$/;   // same, but must not be empty

  const sanitizeNoDigits = (v: string) => v.replace(/\d+/g, '').replace(/\s+/g, ' ').trim();
  const SOURCE_OPTIONS: Option[] = [
    { label: "All Sources", value: "" },
    { label: "BiNi", value: "BiNi" },
    { label: "MailBox", value: "MailBox" },
    { label: "Manually-Candidate", value: "Manually-Candidate" },
    { label: "Manually-HR", value: "Manually-HR" },
  ];
  const DIAL_CODE_OPTIONS: DialOption[] = [
    { label: "India (+91)", value: "+91" },
    { label: "United States (+1)", value: "+1" },
    { label: "United Kingdom (+44)", value: "+44" },
    { label: "Canada (+1)", value: "+1" },
    { label: "Australia (+61)", value: "+61" },
    { label: "Singapore (+65)", value: "+65" },
    { label: "United Arab Emirates (+971)", value: "+971" },
    { label: "Germany (+49)", value: "+49" },
    { label: "France (+33)", value: "+33" },
    { label: "Italy (+39)", value: "+39" },
    { label: "Spain (+34)", value: "+34" },
    { label: "Netherlands (+31)", value: "+31" },
    { label: "Switzerland (+41)", value: "+41" },
    { label: "South Africa (+27)", value: "+27" },
    { label: "New Zealand (+64)", value: "+64" },
    { label: "Japan (+81)", value: "+81" },
  ];

  const ROUNDS_PAGE_SIZE = 5;
  const [roundPage, setRoundPage] = useState(1);

  const openInterviewFromInfoPopup = (interviewId?: number) => {
  if (!interviewId) {
    toast.error("Interview not found for this candidate");
    return;
  }

  // Close resume info modal
  hideBsModal("resume_details_modal");

  // Navigate to Interviews module
  navigate(`/interviews?interviewId=${interviewId}`);
};

  
  // phone
  const [phoneDial, setPhoneDial] = useState<DialOption>(DIAL_CODE_OPTIONS[0]);
  const [phoneLocal, setPhoneLocal] = useState<string>("");
  const loyaltyTitle = (
    <span>
      Loyalty(%){" "}
      <i
        className="ti ti-info-circle ms-1 text-muted"
        style={{ cursor: "pointer" }}
        data-bs-toggle="tooltip"
        data-bs-placement="bottom"
        data-bs-html="false"
        title="Indicates how consistently an employee stays with companies throughout their career. Higher percentages show greater job stability and commitment to employers"
      />
    </span>
  );
  // ADD
  const [skillInput, setSkillInput] = useState("");

  // Allow only letters and spaces
  const SKILL_ALLOWED_RE = /^[A-Za-z0-9.+#\-_/&() ]+$/;

  const normalizeSkills = (raw: string) => {
    const tokens = raw
      .split(/[,;\n]+/)          // comma / semicolon / newline
      .map(s => s.trim().replace(/\s+/g, " "))
      .filter(Boolean);

    const valid = tokens.filter(t => SKILL_ALLOWED_RE.test(t));
    const invalid = tokens.filter(t => !SKILL_ALLOWED_RE.test(t));

    if (invalid.length) {
      toast.warn(`Ignored ${invalid.length} invalid skill${invalid.length > 1 ? "s" : ""} (letters & spaces only).`);
    }
    return valid;
  };


  const addSkill = (raw?: string) => {
    const tokens = normalizeSkills(raw ?? skillInput);
    if (!tokens.length) return;

    setEditData((s: any) => {
      const curr = Array.isArray(s.skills) ? s.skills : [];
      const seen = new Set(curr.map((x: string) => x.toLowerCase()));
      const next = [...curr];

      for (const t of tokens) {
        const key = t.toLowerCase();
        if (!seen.has(key)) {
          next.push(t);
          seen.add(key);
        }
      }
      return { ...s, skills: next };
    });

    setSkillInput("");
  };

  const removeSkill = (idx: number) =>
    setEditData((s: any) => ({
      ...s,
      skills: (s.skills || []).filter((_: any, i: number) => i !== idx),
    }));

  const parseDialAndLocal = (raw?: string) => {
    const txt = String(raw || "").trim();

    // normalize: remove spaces, hyphens, dots, parentheses; allow 00→+ shortcut
    const norm = txt
      .replace(/[()\s\-.]/g, "")
      .replace(/^00/, "+");

    let dial: DialOption | undefined;
    let local = "";

    if (norm.startsWith("+")) {
      // try to match a known dial code (longest first to prefer +971 over +97, etc.)
      const codesDesc = [...DIAL_CODE_OPTIONS.map(o => o.value)].sort(
        (a, b) => b.length - a.length
      );

      const matched = codesDesc.find(code => norm.startsWith(code));
      if (matched) {
        dial = DIAL_CODE_OPTIONS.find(o => o.value === matched);
        local = norm.slice(matched.length).replace(/\D/g, "");
      } else {
        // unknown +code → treat everything after '+' as local
        local = norm.slice(1).replace(/\D/g, "");
      }
    } else {
      // no '+' present → treat entire thing as local number
      local = norm.replace(/\D/g, "");
    }

    // Choose a default dial if none was found (you wanted India by default)
    if (!dial) {
      dial =
        DIAL_CODE_OPTIONS.find(o => o.value === "+91") || DIAL_CODE_OPTIONS[0];
    }

    // clamp to 10 local digits (keeps the FIRST 10, not the last 10)
    return { dial, local: local.slice(0, 10) };
  };
  const todayISO = () => dayjs().format("YYYY-MM-DD");
  const todayLabel = () => dayjs().format("DD-MMM-YYYY");

  // ======== NEW: modal & tabs state ========
  const [showCareerModal, setShowCareerModal] = useState(false);
  type CareerTabKey = "generate" | "upload";
  const [careerActiveTab, setCareerActiveTab] = useState<CareerTabKey>("generate");

  // ======== NEW: generate-link state ========
  type GenLinkResp = { link?: string; expires_at?: string } | null;
  const [genLinkData, setGenLinkData] = useState<GenLinkResp>(null);
  const [genLinkLoading, setGenLinkLoading] = useState(false);

  // ======== NEW: upload state ========
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadResp, setUploadResp] = useState<{
    resumes: any[];
    file_references: Record<string, string>;
  } | null>(null);
  const [savingUploaded, setSavingUploaded] = useState(false);

  // Allowed file types & count
  const ALLOWED_EXTS = [".pdf", ".doc", ".docx", ".docs"]; // per requirement (note: ".docs" isn't common but kept as asked)
  const ALLOWED_MIMES = [
    "application/pdf",
    "application/msword",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  ];
  const MAX_FILES = 10;

  // ======== NEW: open modal helper ========
  const openCareerModal = (initialTab: CareerTabKey = "generate") => {
    setCareerActiveTab(initialTab);
    setShowCareerModal(true);
    // optional: prefetch link when opening on "generate"
    if (initialTab === "generate") {
      void handleGenerateLink();
    }
  };

  // ======== NEW: Generate Link handler ========
  const handleGenerateLink = async () => {
    setGenLinkLoading(true);
    setGenLinkData(null);
    try {
      const res = await fetch(`${REACT_APP_CAREER_API_BASE_URL}/generate_link`, {
        method: "POST",
        credentials: "include", // in case the server expects cookies like in your curl
      });
      if (!res.ok) throw new Error(`Generate link failed (${res.status})`);
      const data = await res.json(); // { link, expires_at }
      setGenLinkData({ link: data?.link, expires_at: data?.expires_at });
    } catch (err: any) {
      toast.error(err?.message || "Failed to generate link");
    } finally {
      setGenLinkLoading(false);
    }
  };

  // ======== NEW: file selection/validation ========
  const validateFiles = (list: FileList | null): File[] => {
    if (!list || list.length === 0) return [];
    const files = Array.from(list);

    // limit count
    const trimmed = files.slice(0, MAX_FILES - selectedFiles.length);
    const valid: File[] = [];
    const rejected: string[] = [];

    for (const f of trimmed) {
      const ext = `.${(f.name.split(".").pop() || "").toLowerCase()}`;
      const mime = f.type;

      const okExt = ALLOWED_EXTS.includes(ext);
      const okMime = mime ? ALLOWED_MIMES.includes(mime) : false;

      // Require valid extension AND (valid mime OR empty mime)
      if (okExt && (okMime || !mime)) {
        valid.push(f);
      } else {
        rejected.push(f.name);
      }
    }

    if (rejected.length) {
      toast.error(`File type not supported: ${rejected.join(", ")}`);
    }

    return valid;
  };


  const onPickFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || !files.length) {
      e.target.value = "";
      return;
    }

    const valid = validateFiles(files);

    // Case 1: Already at limit → reject all
    if (selectedFiles.length >= MAX_FILES) {
      toast.error(`File limit reached. Maximum ${MAX_FILES} files allowed.`);
      e.target.value = "";
      return;
    }

    // Case 2: Adding would exceed → allow partial, show toast
    if (valid.length + selectedFiles.length > MAX_FILES) {
      const spaceLeft = MAX_FILES - selectedFiles.length;
      setSelectedFiles((prev) => [...prev, ...valid.slice(0, spaceLeft)]);
      toast.error(
        `You can only add ${spaceLeft} more file(s). Max ${MAX_FILES} allowed.`
      );
    } else {
      // Case 3: Safe to add
      setSelectedFiles((prev) => [...prev, ...valid]);
    }

    // Always reset input so same file can be picked again
    e.target.value = "";
  };




  const removePickedFile = (idx: number) => {
    // remove file from selectedFiles
    setSelectedFiles((prev) => prev.filter((_, i) => i !== idx));

    // clear parsed resumes when any file is removed
    setUploadResp(null);
  };


  // ======== NEW: Upload handler ========
  const handleUpload = async () => {
    if (!selectedFiles.length) {
      toast.warn("Please select at least one file");
      return;
    }
    setUploading(true);
    setUploadResp(null);
    try {
      const fd = new FormData();
      // backend expects field name = "resumes"
      selectedFiles.forEach((f) => fd.append("resumes", f, f.name));

      const res = await fetch(`${REACT_APP_CAREER_API_BASE_URL}/upload`, {
        method: "POST",
        body: fd,
        credentials: "include", // your curl shows cookies; include them if needed
      });

      if (!res.ok) throw new Error(`Upload failed (${res.status})`);
      const data = await res.json();

    /* ----------------------------
       1. Get duplicate filenames
    -----------------------------*/
   const duplicateFilenames = new Set<string>(
      (data?.duplicates_list ?? []).map((d: any) => d.filename)
    );

    /* ----------------------------
       2. Filter resumes
    -----------------------------*/
    const filteredResumes = (data?.resumes ?? []).filter((resume: any) => {
      if (resume?.is_duplicate) return false;
      if (!resume?.original_filename) return false;
      if (duplicateFilenames.has(resume.original_filename)) return false;
      return true;
    });
    const filteredSelectedFiles = selectedFiles.filter(
      (file) => !duplicateFilenames.has(file.name)
    );

    // 4. Update both states
    setSelectedFiles(filteredSelectedFiles);

     setUploadResp({
      resumes: filteredResumes,
      file_references: data?.file_references || {},
    });

    if (data?.duplicates_detected > 0) {
      toast.warn(`${data.duplicates_detected} duplicate file(s) skipped`);
    } else {
      toast.success("Uploaded successfully");
    }

    } catch (err: any) {
      toast.error(err?.message || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  // ======== NEW: Save (after upload parse) ========
  const handleSaveUpload = async () => {
    if (!uploadResp?.resumes?.length) {
      toast.warn("Nothing to save. Upload and parse resumes first.");
      return;
    }
    setSavingUploaded(true);
    try {
      const res = await fetch(`${REACT_APP_CAREER_API_BASE_URL}/save`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include", // matches curl cookies
        body: JSON.stringify({
          resumes: uploadResp.resumes,
          file_references: uploadResp.file_references,
        }),
      });
      if (!res.ok) throw new Error(`Save failed (${res.status})`);
      const data = await res.json(); // { message: "..."}
      toast.success(String(data?.message || "Saved"));
      // optional: refresh grid after save
      await fetchResumes();
      resetCareerModalState();
      setShowCareerModal(false);
    } catch (err: any) {
      toast.error(err?.message || "Save failed");
    } finally {
      setSavingUploaded(false);
    }
  };



  // pagination & data
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = useDynamicPageSize();
  const [totalItems, setTotalItems] = useState(0);
  const [tableHeight, setTableHeight] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  const [filtersLoading, setFiltersLoading] = useState(true);


  // resumes state
  const [resumesList, setResumesList] = useState<ResumeRow[]>([]);
  const [resumeCategoryId, setResumeCategoryId] = useState<any>("");
  const [resumeCategories, setResumeCategories] = useState<Option[]>([]);
  const [resumeSource, setResumeSource] = useState<string>("");
  const [resumeDateRange, setResumeDateRange] = useState<[Dayjs | null, Dayjs | null]>([null, null]);
  // near other useState()
  const [useDateFilter, setUseDateFilter] = useState(false);
  const [activeDateRange, setActiveDateRange] =
    useState<{ from?: string; to?: string }>({});
  const labelsToIsoRange = (sLabel: string, eLabel: string) => {
    const from = dayjs(sLabel, "DD-MMM-YYYY", true).format("YYYY-MM-DD");
    const to = dayjs(eLabel, "DD-MMM-YYYY", true).format("YYYY-MM-DD");
    return { from, to };
  };

  const handleActivateDateFilterOnClick = () => {
    if (useDateFilter) return; // already active

    const from = oldestAppliedISO || DEFAULT_START_ISO;
    const to = DEFAULT_END_ISO;

    setActiveDateRange({ from, to });
    setSelectedStartLabel(dayjs(from).format("DD-MMM-YYYY"));
    setSelectedEndLabel(dayjs(to).format("DD-MMM-YYYY"));
    setUseDateFilter(true);
    setCurrentPage(1);

    updateResumeFilterUrl({
      search: searchTerm.trim(),
      category_id: resumeCategoryId || "",
      source: resumeSource || "",
      date_from: from,
      date_to: to,
      currentPage: 1,
    });
  };


  const [showDatePicker, setShowDatePicker] = useState(true); // or whatever you use
  const [selectedStartDate, setSelectedStartDate] = useState<string>("");
  const [selectedEndDate, setSelectedEndDate] = useState<string>("");
  // const [selectedStartLabel, setSelectedStartLabel] = useState<string>(
  //   dayjs().startOf("month").format("DD-MMM-YYYY")
  // );
  // const [selectedEndLabel, setSelectedEndLabel] = useState<string>(
  //   dayjs().endOf("month").format("DD-MMM-YYYY")   // ← endOf month (not today)
  // );
  const [selectedStartLabel, setSelectedStartLabel] = useState<string>("");
  const [selectedEndLabel, setSelectedEndLabel] = useState<string>("");
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailData, setDetailData] = useState<CandidateDetails | null>(null);
  const currentMonthStart = dayjs().startOf("month").format("YYYY-MM-DD");
  const currentMonthEnd = dayjs().endOf("month").format("YYYY-MM-DD");
  // table height calc
  useLayoutEffect(() => {
    if (pageSize === 0) return;
    const calc = () => {
      const baseOffset = 194;
      const extraOffset = window.innerWidth <= 991 ? -43 : -81;
      const fixedOffset = baseOffset + extraOffset;
      setTableHeight(window.innerHeight - fixedOffset);
    };
    calc();
    window.addEventListener("resize", calc);
    return () => window.removeEventListener("resize", calc);
  }, [pageSize]);

  const interviews = detailData?.interviews || [];

 // Screening notes = first interview description (if any)
  const screeningNotes =
   interviews.length && interviews[0]?.description?.trim()
    ? interviews[0].description
    : "—";

 // Collect ALL rounds from ALL interviews (safe + future-proof)
  const allRounds = interviews.flatMap((i: any) => i.rounds || []);

  useEffect(() => {
    setRoundPage(1);
  }, [detailData?.id]);
  
  const totalRoundPages = Math.ceil(allRounds.length / ROUNDS_PAGE_SIZE);
  
  const paginatedRounds = allRounds.slice(
    (roundPage - 1) * ROUNDS_PAGE_SIZE,
    roundPage * ROUNDS_PAGE_SIZE
  );
  // ---------- helpers ----------
  const toIso = (label?: string) => {
    if (!label || !label.trim()) return undefined;
    const parsed = dayjs(label, "DD-MMM-YYYY", true);
    return parsed.isValid() ? parsed.format("YYYY-MM-DD") : undefined;
  };

  const getDateRangePayload = () => {
    if (!useDateFilter) return {};
    const out: Record<string, string> = {};
    if (activeDateRange.from) out.date_from = activeDateRange.from;
    if (activeDateRange.to) out.date_to = activeDateRange.to;
    return out;
  };
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    const { date_from = "", date_to = "" } = getDateRangePayload();
    const dr = getDateRangePayload();
    updateResumeFilterUrl({
      search: (searchTerm || "").trim(),
      category_id: resumeCategoryId || "",
      source: resumeSource || "",
      date_from: dr.date_from || "",
      date_to: dr.date_to || "",
      currentPage: page,
    });
  };

  const handleSearchChange = (val: string) => {
    userInitiatedSearchRef.current = true;
    setSearchTerm(val);
    setCurrentPage(1);

    if (debounceTimeoutRef.current) clearTimeout(debounceTimeoutRef.current);
    const dr = getDateRangePayload();
    debounceTimeoutRef.current = setTimeout(() => {
      const filters: ResumeFilters = {
        search: val,
        category_id: resumeCategoryId || "",
        source: resumeSource || "",
        date_from: dr.date_from || "",
        date_to: dr.date_to || "",
        currentPage: 1,
      };

      updateResumeFilterUrl(filters);
      // fetchResumes(undefined, val);
    }, 500);
  };

  useEffect(() => () => {
    if (debounceTimeoutRef.current) clearTimeout(debounceTimeoutRef.current);
  }, []);
  const handleResetFilters = () => {
    setSearchTerm("");
    setResumeCategoryId("");
    setResumeSource("");
    setCurrentPage(1);

    // Reset labels (for display only)
    const fromISO = oldestAppliedISO || DEFAULT_START_ISO;
    const toISO = DEFAULT_END_ISO;
    setSelectedStartLabel(dayjs(fromISO).format("DD-MMM-YYYY"));
    setSelectedEndLabel(dayjs(toISO).format("DD-MMM-YYYY"));

    // Clear date filter completely
    setActiveDateRange({});
    setUseDateFilter(false);

    // Don’t include date_from/date_to in reset payload
    updateResumeFilterUrl({
      search: "",
      category_id: "",
      source: "",
      currentPage: 1,
    });
  };





  // 2) keep your handler unchanged (still strings from PredefinedDateRanges)
  const handleDateRangeChangeFilter = (startLabel: string, endLabel: string) => {
    setUseDateFilter(true);
    const normalize = (v: string) =>
      v && dayjs(v, "DD-MMM-YYYY", true).isValid()
        ? v
        : dayjs(v).isValid()
          ? dayjs(v).format("DD-MMM-YYYY")
          : "";

    const nextStart = normalize(startLabel);
    const nextEnd = normalize(endLabel);

    setSelectedStartLabel(nextStart);
    setSelectedEndLabel(nextEnd);
    setCurrentPage(1);

    const date_from = dayjs(nextStart, "DD-MMM-YYYY").format("YYYY-MM-DD");
    const date_to = dayjs(nextEnd, "DD-MMM-YYYY").format("YYYY-MM-DD");

    setActiveDateRange({ from: date_from, to: date_to });
    // follow oldest only if the chosen FROM == current oldest
    if (oldestAppliedISO && dayjs(date_from).isSame(dayjs(oldestAppliedISO), "day")) {
      followGlobalOldestRef.current = true;
    } else {
      followGlobalOldestRef.current = false;
    }
    updateResumeFilterUrl({
      search: searchTerm,
      category_id: resumeCategoryId || "",
      date_from,
      date_to,
      currentPage: 1,
    });

    // fetchResumes({ date_from, date_to });
  };
  const handleSourceChange = (opt: Option | null) => {
    const v = opt?.value ?? "";
    setResumeSource(v);
    setCurrentPage(1);
    const dr = getDateRangePayload();
    updateResumeFilterUrl({
      search: searchTerm,
      category_id: resumeCategoryId || "",
      source: v,                                  // ← add
      date_from: dr.date_from || "",
      date_to: dr.date_to || "",
      currentPage: 1,
    });
  };

  const handleCategoryChange = (opt: Option | null) => {
    const val = opt?.value ?? "";
    setResumeCategoryId(val);
    setCurrentPage(1);

    const dr = getDateRangePayload();
    updateResumeFilterUrl({
      search: (searchTerm || "").trim(),
      category_id: val || "",
      source: resumeSource || "",
      date_from: dr.date_from || "",
      date_to: dr.date_to || "",
      currentPage: 1,
    });

    // fetchResumes({ category_id: val });
  };

  useEffect(() => {
    if (navCausedRef.current) {
      navCausedRef.current = false;
      return;
    }

    hydratingRef.current = true;
    setFiltersReady(false);

    const [, , encoded] = location.pathname.split("/");
    try {
      if (encoded) {
        const { search, currentPage, category_id, source, date_from, date_to } =
          decryptQuery(decodeURIComponent(encoded)) as ResumeFilters;

        setSearchTerm(search || "");
        setCurrentPage(currentPage ?? 1);
        setResumeCategoryId(category_id ?? "");
        setResumeSource(source ?? "");

        if (date_from || date_to) {
          //  URL explicitly asked for date filter
          setUseDateFilter(true);
          setActiveDateRange({ from: date_from, to: date_to });
          setSelectedStartLabel(dayjs(date_from).format("DD-MMM-YYYY"));
          setSelectedEndLabel(dayjs(date_to).format("DD-MMM-YYYY"));
        } else {
          //  UI shows default range, but no filter is applied
          const fromISO = oldestAppliedISO || DEFAULT_START_ISO;
          const toISO = DEFAULT_END_ISO;
          setUseDateFilter(false);
          setActiveDateRange({});
          setSelectedStartLabel(dayjs(fromISO).format("DD-MMM-YYYY"));
          setSelectedEndLabel(dayjs(toISO).format("DD-MMM-YYYY"));
        }
      } else {
        // no encoded URL → same as above (UI shows, but filter off)
        const fromISO = oldestAppliedISO || DEFAULT_START_ISO;
        const toISO = DEFAULT_END_ISO;
        setSearchTerm("");
        setCurrentPage(1);
        setResumeCategoryId("");
        setResumeSource("");
        setUseDateFilter(false);
        setActiveDateRange({});
        setSelectedStartLabel(dayjs(fromISO).format("DD-MMM-YYYY"));
        setSelectedEndLabel(dayjs(toISO).format("DD-MMM-YYYY"));
      }
    } catch {
      // fallback → safe defaults, filter off
      const fromISO = oldestAppliedISO || DEFAULT_START_ISO;
      const toISO = DEFAULT_END_ISO;
      setSearchTerm("");
      setCurrentPage(1);
      setResumeCategoryId("");
      setResumeSource("");
      setUseDateFilter(false);
      setActiveDateRange({});
      setSelectedStartLabel(dayjs(fromISO).format("DD-MMM-YYYY"));
      setSelectedEndLabel(dayjs(toISO).format("DD-MMM-YYYY"));
    } finally {
      setTimeout(() => {
        hydratingRef.current = false;
        setFiltersReady(true);
      }, 0);
    }
  }, [location.pathname, oldestAppliedISO]);





  const defaultResumeFilters: ResumeFilters = {
    search: "",
    category_id: "",
    date_from: currentMonthStart,
    date_to: currentMonthEnd,
    currentPage: 1,
  };

  const areResumeFiltersDefaultForReset = (f: ResumeFilters) =>
    (f.search ?? "") === "" &&
    (f.category_id ?? "") === "" &&
    (f.source ?? "") === "" &&
    (f.date_from ?? "") === (oldestAppliedISO || DEFAULT_START_ISO) &&
    (f.date_to ?? "") === DEFAULT_END_ISO;


  // ⤴ ignores currentPage so the Reset button won’t show on page 2+

  const areResumeFiltersDefaultForUrl = (f: ResumeFilters) =>
    (f.search ?? "") === "" &&
    (f.category_id ?? "") === "" &&
    (f.source ?? "") === "" &&
    !(f.date_from) &&
    !(f.date_to) &&
    (f.currentPage ?? 1) === 1;
  // ⤴ includes currentPage, so page>1 encodes into URL


  const areResumeFiltersDefault = (f: ResumeFilters) =>
    (f.search ?? "") === "" &&
    (f.category_id ?? "") === "" &&
    (f.date_from ?? "") === DEFAULT_START_ISO &&
    (f.date_to ?? "") === DEFAULT_END_ISO;
  // (f.currentPage ?? 1) === 1;

  // compute whether to show reset
  const activeDates = getDateRangePayload();
  const dr = getDateRangePayload();
  const showReset =
    searchTerm.trim() !== "" ||
    resumeCategoryId !== "" ||
    resumeSource !== "" ||
    useDateFilter; // reset only when date filter is active



  const updateResumeFilterUrl = (filters: ResumeFilters, baseRoute = "/resumes") => {
    // mark that this navigation is caused by our own handlers
    navCausedRef.current = true;

    if (areResumeFiltersDefaultForUrl(filters)) {
      navigate(baseRoute, { replace: false });
    } else {
      const encrypted = encryptQuery(filters);
      const encoded = encodeURIComponent(encrypted);
      navigate(`${baseRoute}/${encoded}`, { replace: false });
    }
  };


  const useDebounce = <T,>(value: T, delay = 400) => {
    const [deb, setDeb] = useState(value);
    useEffect(() => {
      const t = setTimeout(() => setDeb(value), delay);
      return () => clearTimeout(t);
    }, [value, delay]);
    return deb;
  };

  const debouncedSearch = useDebounce(searchTerm, 400);

  const fmtYMShort = (m?: number) => {
    const n = Math.max(0, Number(m) || 0);
    const y = Math.floor(n / 12);
    const r = n % 12;
    if (y && r) return `${y}y ${r}m`;
    if (y) return `${y}y`;
    return `${r}m`;
  };

  const fmtMonths = (m?: number) => {
    const n = Number(m) || 0;
    const y = Math.floor(n / 12);
    const r = n % 12;
    if (y && r) return `${y} year${y > 1 ? 's' : ''} ${r} month${r > 1 ? 's' : ''}`;
    if (y) return `${y} year${y > 1 ? 's' : ''}`;
    return `${r} month${r !== 1 ? 's' : ''}`;
  };

  const gmailComposeUrl = (to?: string) =>
    `https://mail.google.com/mail/?view=cm&fs=1&to=${encodeURIComponent(to || "")}`;

  // Priority: explicit string → record.contact_no/contact → dial+local → local → dial
  const resolvePhoneText = (
    raw?: string | { contact_no?: string; contact?: string },
    dial?: string,
    local?: string
  ): string => {
    // 1) direct string
    if (typeof raw === "string" && raw.trim()) return raw.trim();

    // 2) record fields
    if (raw && typeof raw === "object") {
      const combined = String(raw.contact_no || raw.contact || "").trim();
      if (combined) return combined;
    }

    // 3) edit UI pieces (e.g., from state)
    const d = (dial || "").trim();
    const l = (local || "").trim();
    if (d && l) return `${d} ${l}`;
    if (l) return l;
    if (d) return d;

    // 4) nothing
    return "";
  };


  const copyToClipboard = async (raw?: string | { contact_no?: string; contact?: string }) => {
    // Use your module-scoped phoneDial/phoneLocal if available
    const val = resolvePhoneText(raw, phoneDial?.value, phoneLocal);
    if (!val) {
      console.log("did not copied number!!");
      toast.error("Nothing to copy");
      return;
    }

    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(val);
        console.log(val);
        toast.success("Contact Copied");
        return;
      }
    } catch {
      // fall through
      console.log("error");
    }

    try {
      const ta = document.createElement("textarea");
      ta.value = val;
      ta.setAttribute("readonly", "");
      ta.style.position = "fixed";
      ta.style.top = "-10000px";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.focus();
      ta.select();
      ta.setSelectionRange(0, ta.value.length);
      const ok = document.execCommand("copy");
      document.body.removeChild(ta);
      if (ok) toast.success("Contact Copied");
      else throw new Error("execCommand failed");
    } catch (e) {
      console.error(e);
      toast.error("Unable to copy");
      console.log("error");
    }
  };



  // Format: "+91 9876543210" (falls back gracefully)
  const formatDialLocal = (dial?: string, local?: string) => {
    const d = (dial || "").trim();
    const l = (local || "").trim();
    if (d && l) return `${d} ${l}`;
    if (d) return d;
    return l || "";
  };

  // Try to build a full phone from whatever the record has
  const getFormattedPhone = (rec: any) => {
    // 1) If API already gives combined contact_no, prefer it
    const combined = String(rec?.contact_no || rec?.contact || "").trim();
    if (combined) return combined;

    // 2) If we’re in edit flow, use local state (phoneDial/phoneLocal)
    // (safe even outside edit since these are in module scope)
    if (typeof phoneDial?.value === "string" || typeof phoneLocal === "string") {
      const candidate = formatDialLocal(phoneDial?.value, phoneLocal);
      if (candidate) return candidate;
    }

    // 3) Last resort: empty
    return "";
  };

  const handleViewDetails = async (id: number) => {
    // open modal immediately (shows spinner)
    setDetailData(null);
    setDetailLoading(true);
    showBsModal("resume_details_modal");

    try {
      const res = await nodeApi<any>("/candidates/details", {
        method: "POST",
        body: { id },
        // headers: { Authorization: `Bearer ${process.env.REACT_APP_TOKEN || ""}` },
      });

      setDetailData(res?.data || res); // support either shape
    } catch (e: any) {
      toast.error(e?.message || "Failed to load details");
      // close modal on failure
      hideBsModal("resume_details_modal");
    } finally {
      setDetailLoading(false);
    }
  };

  // --- Modal helpers (Bootstrap) ---
  const showBsModal = (id: string) => {
    const el = document.getElementById(id);
    if (!el) return;
    const bsModal = (window as any).bootstrap.Modal.getOrCreateInstance(el);
    bsModal.show();
  };

  const hideBsModal = (id: string) => {
    const el = document.getElementById(id);
    if (!el) return;
    const bsModal = (window as any).bootstrap.Modal.getInstance(el)
      ?? (new (window as any).bootstrap.Modal(el));
    bsModal.hide();
    setTimeout(() => {
      document.querySelectorAll(".modal-backdrop").forEach((b) => b.remove());
      document.body.classList.remove("modal-open");
      document.body.style.overflow = "";
    }, 300);
  };

  const handleOpenEdit = async (id: number) => {
    try {
      const res = await nodeApi<any>("/candidates/details", { method: "POST", body: { id } });
      const d = res?.data || res;
      const totalMonths = Number(d.total_experience_months) || 0;
      const years = Math.floor(totalMonths / 12);
      const months = totalMonths % 12;

      // try to resolve category_id from API; if not present, match by name from the list
      const resolvedCatId =
        d.category_id ??
        (resumeCategories.find((c) => c.label === d.category_name)?.value ?? "");

      setEditOriginalName(d?.name || "");
      setEditData({
        id: d.id,
        name: d.name || "",
        email: d.email || "",
        contact_no: d.contact_no || d.contact || "",
        current_location: d.current_location || "",
        designation: d.designation || "",
        source: d.source || "",
        skills: Array.isArray(d.skills)
          ? d.skills
          : (d.skills ? String(d.skills).split(",").map((s: string) => s.trim()).filter(Boolean) : []),
        ctc_info: d.ctc_info || "",
        companies_worked: d.companies_worked ?? "",
        last_company: d.last_company || "",
        loyalty_percentage: d.loyalty_percentage ?? "",
        date: d?.date ? dayjs(d.date).format("YYYY-MM-DD") : "",
        category_id: resolvedCatId,
        category_name: d.category_name || "",
        total_experience_months: d.total_experience_months ?? "",
        exp_years: years,
        exp_months: months,
        file_name: d.file_name || "",
      });
      // init the dial + local pieces from the stored contact
      const parsed = parseDialAndLocal(d.contact_no || d.contact);
      setPhoneDial(parsed.dial);
      setPhoneLocal(parsed.local);
      showBsModal("resume_edit_modal");
    } catch (e: any) {
      toast.error(e?.message || "Failed to open edit");
    }
  };

  useEffect(() => {
    if (!editData) return;
    setEditData((s: any) => ({
      ...s,
      contact_no: phoneLocal ? `${phoneDial.value} ${phoneLocal}` : "",
    }));
  }, [phoneDial, phoneLocal]); // eslint-disable-line react-hooks/exhaustive-deps


  const validateEditData = (d: any): string | null => {
    const name = String(d?.name || '').trim();
    const email = String(d?.email || '').trim();
    const designation = String(d?.designation || '').trim();
    const source = String(d?.source || '').trim();

    // Name: required, no digits
    if (!NO_DIGITS_REQUIRED_RE.test(name)) {
      return "Name is required and cannot contain numbers.";
    }

    // Email: required + global-ish format
    if (!email || !EMAIL_RE.test(email)) {
      return "Please enter a valid email address (e.g., name@example.com).";
    }

    // Designation / Source: optional, but if provided they cannot contain numbers
    if (designation && !NO_DIGITS_OPTIONAL_RE.test(designation)) {
      return "Designation cannot contain numbers.";
    }
    if (source && !NO_DIGITS_OPTIONAL_RE.test(source)) {
      return "Source cannot contain numbers.";
    }

    return null;
  };

  const handleSaveEdit = async () => {
    if (!editData) return;

    // Final trim/sanitize just before save
    const payload = {
      ...editData,
      name: sanitizeNoDigits(String(editData.name || '')),
      designation: sanitizeNoDigits(String(editData.designation || '')),
      source: sanitizeNoDigits(String(editData.source || '')),
      ctc_info: editData.ctc_info || "",
      email: String(editData.email || '').trim(),
    };

    const err = validateEditData(payload);
    if (err) {
      toast.error(err);
      return;
    }
    const ctc = String(payload.ctc_info || "").trim();

    if (ctc !== "") {
      if (!CTC_REGEX.test(ctc)) {
        toast.error("Invalid CTC format. Use e.g. '10 LPA'");
        return;
      }

      if (!isCTCInRange(ctc)) {
        toast.error("CTC must be between 0.5 LPA and 100 LPA.");
        return;
      }
    }



    setEditSaving(true);
    try {
      const resp = await nodeApi<any>("/candidates/details", {
        method: "PUT",
        body: payload,
      });
      const duplicated = !!resp?.duplicated;
      const newId = resp?.data?.id;
      toast.success(duplicated ? `New candidate created (ID: ${newId}).` : "Candidate updated.");
      hideBsModal("resume_edit_modal");
      await fetchResumes(); // refresh list
    } catch (e: any) {
      toast.error(e?.message || "Failed to save");
    } finally {
      setEditSaving(false);
    }
  };

  const handleOpenResume = async (id: number) => {
    try {
      // if you already fetched detailData for this id, reuse it.
      const res = await nodeApi<any>("/candidates/details", {
        method: "POST",
        body: { id },
      });
      const d = res?.data || res;
      const url = d?.file_url || d?.file_name; // prefer a fully-qualified URL
      if (!url) return toast.error("Resume link not found");
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (e: any) {
      toast.error(e?.message || "Failed to open resume");
    }
  };

  // SAME PATTERN as your fetchDesignations
  const fetchResumes = async (
    overrides?: Partial<{ category_id: any; date_from: string; date_to: string }>,
    searchOverride?: string
  ) => {
    try {
      setLoading(true);
      if (!oldestFetchedRef.current) {
        setFiltersLoading(true);   // skeleton only on first load
      }

      const payload: any = {
        currentPage,
        pageSize,
        sort_by: "date",   // server must sort on the 'date' field
        sort_dir: "desc",
        search: (searchOverride ?? searchTerm) || "",
        category_id: (overrides?.category_id ?? resumeCategoryId) || undefined,
        source: resumeSource || undefined,
      };
      const finalFrom = overrides?.date_from ?? activeDateRange.from;
      const finalTo = overrides?.date_to ?? activeDateRange.to;

      if (useDateFilter && (finalFrom || finalTo)) {
        if (finalFrom) payload.date_from = finalFrom;
        if (finalTo) payload.date_to = finalTo;
      }

      const res = await nodeApi<{ data: any; meta?: any; oldest_date?: string }>("/candidates/list", { method: "POST", body: payload });
      const apiData: any = res;

      const rows = Array.isArray(apiData.data) ? apiData.data : [];
      if (apiData.oldest_date) {
        setOldestDate(dayjs(apiData.oldest_date).format("YYYY-MM-DD"));
      }
      setResumesList(rows);
      setTotalItems(apiData.meta?.total ?? apiData.total ?? 0);

      // Use the GLOBAL oldest date from server meta, just once.
      // Use the GLOBAL oldest date from server meta, just once (and on change).
      const serverOldest = apiData?.meta?.oldest_date; // "YYYY-MM-DD"
      const minISO =
        serverOldest && dayjs(serverOldest, "YYYY-MM-DD", true).isValid()
          ? serverOldest
          : null;

      if (minISO) {
        const firstTime = !oldestAppliedISO;
        const changed = oldestAppliedISO && minISO !== oldestAppliedISO;

        if (firstTime || changed) {
          setOldestAppliedISO(minISO);
          oldestFetchedRef.current = true;
          setFiltersLoading(false);

          // Keep labels in sync if the user hasn't forced a custom start
          if (!useDateFilter || followGlobalOldestRef.current) {
            // Start label should reflect the global oldest
            setSelectedStartLabel(dayjs(minISO).format("DD-MMM-YYYY"));

            if (useDateFilter) {
              const nextTo = activeDateRange.to || DEFAULT_END_ISO;
              setActiveDateRange({ from: minISO, to: nextTo });
              setSelectedEndLabel(dayjs(nextTo).format("DD-MMM-YYYY"));
            }
          }
        }
      }


      // On the very first successful fetch (and not while hydrating from URL),
      // force the date filter to be [GLOBAL oldest .. today] and sync labels + URL



      if (Array.isArray(apiData.categories)) {
        setResumeCategories(apiData.categories.map((c: any) => ({ label: c.name, value: c.id })));
      }
    } catch (err: any) {
      console.error("Resumes fetch error:", err?.message || err);
      setResumesList([]);
      setTotalItems(0);
      toast.error("Failed to load resumes");
    } finally {
      setLoading(false);
      setFiltersLoading(false);
    }
  };





  // initial load + re-fetch on pagination
  // ONE place that actually fetches (state → request)
  // Runs only when filtersReady and the request key changed.
  useEffect(() => {
    if (!filtersReady) return;

    const date_from = getDateRangePayload().date_from || DEFAULT_START_ISO;
    const date_to = getDateRangePayload().date_to || DEFAULT_END_ISO;
    const dr = getDateRangePayload();

    const requestKey = JSON.stringify({
      currentPage,
      pageSize,
      search: (debouncedSearch || "").trim(),
      category_id: resumeCategoryId || "",
      source: resumeSource || "",
      useDateFilter,                        //  include the switch
      date_from: dr.date_from || null,      // null = not applied
      date_to: dr.date_to || null,
    });

    if (requestKey === lastQueryRef.current) return;
    lastQueryRef.current = requestKey;

    fetchResumes(
      useDateFilter
        ? { category_id: resumeCategoryId || "", date_from: dr.date_from!, date_to: dr.date_to! }
        : { category_id: resumeCategoryId || "" },
      debouncedSearch
    );
  }, [
    currentPage,
    pageSize,
    resumeCategoryId,
    resumeSource,
    selectedStartLabel,
    selectedEndLabel,
    debouncedSearch,
    filtersReady,
    useDateFilter,
    activeDateRange.from, // ⬅ track canonical dates
    activeDateRange.to,
  ]);


  useEffect(() => {
    if (!oldestDate) return;
    if (initializedFromOldestRef.current) return;
    initializedFromOldestRef.current = true;

    const today = dayjs().format("YYYY-MM-DD");

    setSelectedStartLabel(dayjs(oldestDate).format("DD-MMM-YYYY"));
    setSelectedEndLabel(dayjs(today).format("DD-MMM-YYYY"));

    setActiveDateRange({});
    setUseDateFilter(false);

    updateResumeFilterUrl({
      search: "",
      category_id: "",
      source: "",
      currentPage: 1,
    });
  }, [oldestDate]);






  // ---------- header filters node (useMemo at top-level, NOT inside effects) ----------
  const resumesFiltersNode = useMemo(
    () => (
      <ResumesFilters
        searchTerm={searchTerm}
        onSearchTermChange={handleSearchChange}
        categoryOptions={resumeCategories}
        selectedCategory={resumeCategoryId}
        onCategoryChange={handleCategoryChange}
        startLabel={selectedStartLabel}
        endLabel={selectedEndLabel}
        onDateRangeChange={handleDateRangeChangeFilter}
        onResetFilters={handleResetFilters}
        showReset={showReset}
        sourceOptions={SOURCE_OPTIONS}
        selectedSource={resumeSource}
        onSourceChange={handleSourceChange}
        onDatePickerClick={handleActivateDateFilterOnClick}
        onPlusClick={() => openCareerModal("generate")}
        oldestAppliedISO={oldestAppliedISO}
        filtersLoading={filtersLoading}
      />
    ),
    [
      searchTerm,
      resumeCategories,
      resumeCategoryId,
      selectedStartLabel,
      selectedEndLabel,
      showReset,
      resumeSource,
      filtersLoading,
    ]
  );

  // push header filters into header context
  useEffect(() => {
    setFilters(resumesFiltersNode);
    return () => setFilters(null);
  }, [resumesFiltersNode, setFilters]);

  useEffect(() => {
    const Tooltip = (window as any)?.bootstrap?.Tooltip;
    if (!Tooltip) return;

    // Desktop (mouse/trackpad) => hover; Mobile/tablet => click (tap)
    const prefersHover =
      typeof window.matchMedia === "function" &&
      window.matchMedia("(hover: hover) and (pointer: fine)").matches;

    const trigger = prefersHover ? "hover focus" : "click";

    const els = Array.from(
      document.querySelectorAll<HTMLElement>('[data-bs-toggle="tooltip"]')
    );

    const instances = els.map((el) => new Tooltip(el, { trigger, container: "body" }));

    const hideAll = () => instances.forEach((i: any) => i.hide());

    // For click-triggered (mobile) tooltips, hide when tapping outside or pressing ESC
    const onDocClick = (e: MouseEvent) => {
      if (prefersHover) return; // not needed for hover tooltips
      const t = e.target as Node;
      els.forEach((el, idx) => {
        const tip = instances[idx] as any;
        const id = el.getAttribute("aria-describedby");
        const tipEl = id ? document.getElementById(id) : null;
        if (el.contains(t) || (tipEl && tipEl.contains(t))) return;
        tip.hide();
      });
    };

    const onEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") hideAll();
    };

    if (!prefersHover) document.addEventListener("click", onDocClick, true);
    document.addEventListener("keydown", onEsc);

    return () => {
      if (!prefersHover) document.removeEventListener("click", onDocClick, true);
      document.removeEventListener("keydown", onEsc);
      instances.forEach((i: any) => i.dispose());
    };
  }, [resumesFiltersNode]); // keep your existing dep if needed



  // columns for resumes
  const resumeColumns = [
    {
      title: "Date Applied", dataIndex: "date",
      render: (t: string) => {
        return t ? dayjs(t).format("DD-MMM-YYYY") : "—";
      },
    },
    {
      title: "Name",
      dataIndex: "name",
      render: (_: any, rec: ResumeRow) => {
        const maxNameLength = 15;
        const maxDesignationLength = 20;

        const truncateText = (text: string, maxLength: number) =>
          text && text.length > maxLength
            ? text.substring(0, maxLength) + "..."
            : text;

        return (
          <div style={{ minWidth: 0 }}>
            <a
              href="#"
              className="fw-medium text-decoration-none"
              onClick={(e) => {
                e.preventDefault();
                handleViewDetails(rec.id); // same details modal as the View icon
              }}
              title="Open candidate details"
            >
              {truncateText(rec.name, maxNameLength)}
            </a>

            {/* email on its own line, ellipsized + tooltip */}
            <div className="text-muted small">
              {rec.email ? (
                <span
                  className="fs-12 fw-normal clickable-copy"
                  title="Click to copy"
                  onClick={() => {
                    navigator.clipboard.writeText(rec.email)
                      .then(() => toast.success("Email copied"))
                      .catch(() => toast.error("Failed to copy"));
                  }}
                >
                  {truncateText(rec.email, maxDesignationLength)}
                </span>
              ) : "—"}
            </div>

            {/* contact on a new line below email
                {rec.contact && (
                  <div className="text-muted small">{truncateText(rec.contact || "-",maxDesignationLength)}</div>
                )} */}
          </div>
        );
      },
    },

    //    { title: "Email Id", dataIndex: "email" ,
    //   render: (t: string) => {
    //     return t;
    //   },},
    {
      title: "Contact",
      dataIndex: "contact",
      render: (t: string, rec: ResumeRow) => {
        if (!t) return "—";

        // SPLIT into multiple numbers
        const parts = String(t)
          .split(/[,/||]+/)            // split by comma or slash
          .map(p => p.trim())
          .filter(Boolean);

        return (
          <div style={{ display: "flex", flexDirection: "column" }}>
            {parts.map((num, i) => (
              <span
                key={i}
                className="clickable-copy"
                title="Click to copy"
                onClick={() => copyToClipboard(num)}
                style={{ cursor: "pointer", marginBottom: i < parts.length - 1 ? 2 : 0 }}
              >
                {num}
              </span>
            ))}
          </div>
        );
      },
    },
    {
      title: "Category",
      dataIndex: "category_name",
      render: (_: any, rec: ResumeRow) => {
        const maxCategoryLength = 14; // same style as designation max
        const text = rec.category_name || "";

        const truncateText = (text: string, maxLength: number) =>
          text && text.length > maxLength
            ? text.substring(0, maxLength) + "..."
            : text;

        return (
          <div style={{ minWidth: 0 }}>
            <span title={text}>
              {truncateText(text, maxCategoryLength)}
            </span>
          </div>
        );
      },
    }

    ,
    {
      title: "Total Exp", dataIndex: "total_experience_months",
      render: (m: number) => {
        return fmtYMShort(m);
      },
    },
    {
      title: "Designation", dataIndex: "designation",
      render: (_: any, rec: ResumeRow) => {
        const maxNameLength = 15;
        const maxDesignationLength = 20;

        const truncateText = (text: string, maxLength: number) =>
          text && text.length > maxLength
            ? text.substring(0, maxLength) + "..."
            : text;

        return (
          <div style={{ minWidth: 0 }}>
            <span
              title={rec.designation || ""}
            >
              {truncateText(rec.designation, maxNameLength)}
            </span>
          </div>
        );
      },
    },
    {
      title: "Source", dataIndex: "source",
      render: (t: string) => {
        return t;
      },
    },
    {
      title: loyaltyTitle, dataIndex: "loyalty_percentage",
      render: (t: string) => {
        return t;
      },
    },
    // { title: "Companies Worked", dataIndex: "companies_worked" ,
    //   render: (t: string) => {
    //     return t;
    //   },},
    // { title: "Last Company", dataIndex: "last_company",
    //   render: (t: string) => {
    //     return t;
    //   },},

    {
      title: "Action",
      dataIndex: "actions",
      render: (_: any, rec: ResumeRow) => (
        <div className="action-icon d-inline-flex">
          <a href="#" onClick={(e) => { e.preventDefault(); handleViewDetails(rec.id); }} title="View">
            <i className="ti ti-eye" />
          </a>
          <a href="#" onClick={(e) => { e.preventDefault(); handleOpenResume(rec.id); }} title="Resume Link">
            <i className="ti ti-file-text" />
          </a>
          <a href="#" onClick={(e) => { e.preventDefault(); handleOpenEdit(rec.id); }} title="Edit">
            <i className="ti ti-edit" />
          </a>
        </div>
      ),
    },

  ];
  // initials for avatar
  const initialsOf = (name?: string) =>
    (name || "")
      .split(" ")
      .filter(Boolean)
      .slice(0, 2)
      .map((s) => s[0]?.toUpperCase())
      .join("");



  return (
    <>
      <div className="page-wrapper d-flex flex-column">
        <div className="content flex-grow-1">
          <div className="card card-margin-bottom">
            <div
              className="card-body p-0"
              id="Resumes-list"
              style={{ height: tableHeight, display: "flex", flexDirection: "column" }}
            >
              <Table
                style={{ flex: 1 }}
                dataSource={Array.isArray(resumesList) ? resumesList : []}
                columns={resumeColumns}
                onRowClick={undefined}
                onPageChange={handlePageChange}
                totalItems={totalItems}
                pageSize={pageSize}
                currentPage={currentPage}
                loading={loading}
              />
            </div>
            {/* Details Modal */}
            <div className="modal fade" id="resume_details_modal" tabIndex={-1}>
              <div className="modal-dialog modal-lg modal-dialog-centered">
                <div className="modal-content border-0 shadow-lg">
                  <div className="modal-header border-0 pb-0">
                    <div className="d-flex align-items-center gap-3">
                      {/* Professional avatar with initials */}
                      <div
                        className="d-flex align-items-center justify-content-center text-white"
                        style={{
                          width: 56,
                          height: 56,
                          borderRadius: "14px",
                          background:
                            "linear-gradient(135deg, #3b82f6 0%, #0ea5e9 100%)",
                          fontSize: 20,
                          fontWeight: 600,
                        }}
                        aria-hidden
                      >
                        {initialsOf(detailData?.name) || "CN"}
                      </div>

                      <div>
                        <h5 className="modal-title mb-0">{detailData?.name || "Candidate"}</h5>
                        <div className="text-muted small">
                          {detailData?.designation || "—"} · {detailData?.current_location || "—"}
                        </div>
                      </div>
                    </div>
                    <button
                      type="button"
                      className="btn-close"
                      data-bs-dismiss="modal"
                      aria-label="Close"
                      onClick={() => hideBsModal("resume_details_modal")}
                    />
                  </div>

                  <div className="modal-body pt-3">
                    {/* Quick actions */}
                    <div className="d-flex flex-wrap gap-2 mb-3">
                      <button
                        className="btn btn-sm btn-primary"
                        onClick={() => handleOpenResume(detailData?.id)}
                      >
                        <i className="ti ti-file-text me-1" /> Open Resume
                      </button>

                      {/* Gmail compose */}
                      {detailData?.email && (
                        <a
                          className="btn btn-sm btn-outline-secondary"
                          href={gmailComposeUrl(detailData.email)}
                          target="_blank"
                          rel="noreferrer"
                        >
                          <i className="ti ti-mail me-1" /> Email
                        </a>
                      )}

                      {/* Copy phone */}
                      {detailData?.contact_no && (
                        <button
                          className="btn btn-sm btn-outline-secondary"
                          onClick={() => copyToClipboard(detailData.contact_no)}
                        >
                          <i className="ti ti-phone me-1" /> Phone
                        </button>
                      )}
                      {/* Interview rounds */}
                       <button
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => openInterviewFromInfoPopup(interviews[0]?.id)}
                        title="Open full interview details"
                      >
                        <i className="ti ti-external-link me-1" />
                        Open Interview
                      </button>
                    </div>

                    {/* Key facts */}
                    {detailLoading && <div>Loading...</div>}
                    {!detailLoading && (
                      <div className="row g-3">
                        <div className="col-12 col-md-6">
                          <div className="card h-100 border-0 shadow-sm">
                            <div className="card-body">
                              <h6 className="text-uppercase text-muted mb-3">Profile</h6>
                              <dl className="row mb-0 small">
                                <dt className="col-5 text-muted">Email</dt>
                                <dd className="col-7">
                                  {detailData?.email ? (
                                    <span
                                      className="text-primary"
                                      style={{ cursor: "pointer" }}
                                      title="Click to copy"
                                      onClick={() => {
                                        navigator.clipboard
                                          .writeText(detailData.email)
                                          .then(() => toast.success("Email copied"))
                                          .catch(() => toast.error("Failed to copy"));
                                      }}
                                    >
                                      {detailData.email}
                                    </span>
                                  ) : (
                                    "—"
                                  )}
                                </dd>
                                <dt className="col-5 text-muted">Contact</dt>
                                <dd className="col-7">
                                  {detailData?.contact_no ? (
                                    <span
                                      className="text-primary"
                                      style={{ cursor: "pointer" }}
                                      title="Click to copy"
                                      onClick={() => copyToClipboard(detailData.contact_no)}
                                    >
                                      {detailData.contact_no}
                                    </span>
                                  ) : (
                                    "—"
                                  )}
                                </dd>
                                <dt className="col-5 text-muted">Experience</dt>
                                <dd className="col-7">
                                  {(() => {
                                    const n = Number(detailData?.total_experience_months) || 0;
                                    const y = Math.floor(n / 12);
                                    const r = n % 12;
                                    if (y && r) return `${y} year${y > 1 ? "s" : ""} ${r} month${r > 1 ? "s" : ""}`;
                                    if (y) return `${y} year${y > 1 ? "s" : ""}`;
                                    return `${r} month${r !== 1 ? "s" : ""}`;
                                  })()}
                                </dd>

                                <dt className="col-5 text-muted">Source</dt>
                                <dd className="col-7">{detailData?.source || "—"}</dd>

                                <dt className="col-5 text-muted">CTC</dt>
                                <dd className="col-7">{detailData?.ctc_info || "—"}</dd>
                              </dl>
                            </div>
                          </div>
                        </div>

                        <div className="col-12 col-md-6">
                          <div className="card h-100 border-0 shadow-sm">
                            <div className="card-body">
                              <h6 className="text-uppercase text-muted mb-3">Career</h6>
                              <dl className="row mb-0 small">
                                <dt className="col-5 text-muted">Companies Worked</dt>
                                <dd className="col-7">{detailData?.companies_worked ?? "—"}</dd>

                                <dt className="col-5 text-muted">Last Company</dt>
                                <dd className="col-7">{detailData?.last_company || "—"}</dd>

                                <dt className="col-5 text-muted">Category</dt>
                                <dd className="col-7">{detailData?.category_name || "—"}</dd>

                                <dt className="col-5 text-muted">Loyalty</dt>
                                <dd className="col-7">
                                  {detailData?.loyalty_percentage != null ? `${detailData.loyalty_percentage}%` : "—"}
                                </dd>
                              </dl>
                            </div>
                          </div>
                        </div>

                      {/* Skills */}
                        <div className="col-12">
                          <div className="card border-0 shadow-sm">
                            <div className="card-body">
                              <h6 className="text-uppercase text-muted mb-3">Skills</h6>
                              <div className="d-flex flex-wrap gap-2">
                                {(Array.isArray(detailData?.skills)
                                  ? detailData.skills
                                  : detailData?.skills
                                    ? String(detailData.skills).split(",")
                                    : []
                                ).map((s: string, i: number) => (
                                  <span key={i} className="badge bg-light text-dark border">
                                    {s.trim()}
                                  </span>
                                ))}
                                {(!detailData?.skills ||
                                  (Array.isArray(detailData.skills) && detailData.skills.length === 0)) && (
                                    <span className="text-muted small">—</span>
                                  )}
                              </div>
                            </div>
                          </div>
                        </div>
                        
                     {/* Screening Notes */}
                              <div className="col-12 mt-0  pt-0">
                                <div className="card border-0 shadow-sm mt-0">
                                  <div className="card-body">
                                    <h6 className="text-uppercase text-muted mb-3">
                                      Screening Notes
                                    </h6>

                                    <div className="small text-dark" style={{ whiteSpace: "pre-wrap" }}>
                                      {screeningNotes}
                                    </div>

                                  </div>
                                </div>
                              </div>

                    {/* Interview Rounds Card */}
                              <div className="col-12 mt-0  pt-0">
                                <div className="card border-0 shadow-sm">
                                  <div className="card-body">

                                    <h6 className="text-uppercase text-muted mb-3">
                                      Interview Rounds
                                    </h6>

                                    {allRounds.length ? (
                                      <div className="table-responsive">
                                        <table className="table table-sm align-middle mb-0">
                                          <thead className="table-light small">
                                            <tr>
                                              <th>Date</th>
                                              <th>Time</th>
                                              <th>Round</th>
                                              <th>Status</th>
                                              <th>Interviewers</th>
                                              
                                            </tr>
                                          </thead>

                                          <tbody className="small">
                                            {paginatedRounds.map((round: any, index: number) => (
                                              <tr key={round.id}>

                                                 {/* Date */}
                                               <td>
                                                  {round.scheduled_datetime
                                                    ? new Date(round.scheduled_datetime)
                                                        .toLocaleDateString("en-GB", {
                                                          day: "2-digit",
                                                          month: "short",
                                                          year: "numeric",
                                                        })
                                                        .replace(/ /g, "-")
                                                    : "—"}
                                                </td>
                                                <td>
                                                    {round.scheduled_datetime
                                                      ? new Date(round.scheduled_datetime).toLocaleTimeString("en-US", {
                                                          hour: "2-digit",
                                                          minute: "2-digit",
                                                          hour12: true,
                                                        })
                                                      : "—"}
                                                  </td>

                                                
                                                {/* Round Name / Type */}
                                                <td className="fw-semibold">
                                                  {round.interview_type || `Round ${index + 1}`}
                                                </td>

                                                {/* Status */}
                                                <td>
                                                  <span
                                                    className={`badge ${
                                                      round.status === "Scheduled"
                                                        ? "bg-warning-subtle text-warning"
                                                        : round.status === "Completed"
                                                        ? "bg-success-subtle text-success"
                                                        : round.status === "Cancelled"
                                                        ? "bg-danger-subtle text-danger"
                                                        : round.status === "Rescheduled"
                                                        ? "bg-info-subtle text-info"
                                                        : "bg-secondary-subtle text-secondary"
                                                    }`}
                                                  >
                                                    {round.status}
                                                  </span>
                                                </td>

                                                {/* Interviewers */}
                                                <td>
                                                  {round.interviewers?.length
                                                    ? round.interviewers
                                                        .map((i: any) => i.interviewer?.name)
                                                        .join(", ")
                                                    : "—"}
                                                </td>
                                              </tr>
                                            ))}
                                          </tbody>
                                        </table>
                                      </div>
                                    ) : (
                                      <div className="text-muted small">
                                        No interview rounds found
                                      </div>
                                    )}
                                    {totalRoundPages > 1 && (
                                      <div className="d-flex justify-content-between align-items-center mt-3">
                                        <small className="text-muted">
                                          Page {roundPage} of {totalRoundPages}
                                        </small>

                                        <div className="btn-group btn-group-sm">
                                          <button
                                            className="btn btn-outline-secondary"
                                            disabled={roundPage === 1}
                                            onClick={() => setRoundPage((p) => p - 1)}
                                          >
                                            Prev
                                          </button>

                                          <button
                                            className="btn btn-outline-secondary"
                                            disabled={roundPage === totalRoundPages}
                                            onClick={() => setRoundPage((p) => p + 1)}
                                          >
                                            Next
                                          </button>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                      </div>
                    )}
                  </div>

                  <div className="modal-footer border-0">
                    <button className="btn btn-primary" onClick={() => hideBsModal("resume_details_modal")}>
                      Close
                    </button>
                    {/* <button className="btn btn-primary" onClick={() => handleOpenResume(detailData?.id)}>
                      <i className="ti ti-file-text me-1" /> Open Resume
                    </button> */}
                  </div>
                </div>
              </div>
            </div>
            {/* Edit Modal */}
            <div className="modal fade" id="resume_edit_modal" tabIndex={-1}>
              <div className="modal-dialog modal-lg modal-dialog-centered">
                <div className="modal-content border-0 shadow-lg">
                  <div className="modal-header border-0">
                    <h5 className="modal-title">Edit Candidate Details</h5>
                    <button type="button" className="btn-close" onClick={() => hideBsModal("resume_edit_modal")} />
                  </div>
                  <div className="modal-body">
                    {!editData ? (
                      <div>Loading…</div>
                    ) : (
                      <form className="row g-3">
                        <div className="col-md-6">
                          <label className="form-label">Date Applied</label>
                          <div className="input-icon-end position-relative">
                            <DatePicker
                              className="form-control datetimepicker ps-15"
                              value={editData.date ? dayjs(editData.date) : null}   // state is ISO → convert to dayjs
                              format="DD-MMM-YYYY"
                              placeholder="DD-MMM-YYYY"
                              onChange={(date) =>
                                setEditData((s: any) => ({
                                  ...s,
                                  date: date ? date.format("YYYY-MM-DD") : "",      // save ISO for API/DB
                                }))
                              }
                            />
                            <span className="input-icon-addon">
                              <i className="ti ti-calendar text-gray-7" />
                            </span>
                          </div>
                        </div>

                        <div className="col-md-6">
                          <label className="form-label">Name</label>
                          <input
                            className="form-control"
                            value={editData.name}
                            onChange={(e) =>
                              setEditData((s: any) => ({ ...s, name: e.target.value }))
                            }
                            onBeforeInput={(e: any) => {
                              if (/\d/.test(e?.data || '')) e.preventDefault();   // block digits
                            }}
                            onPaste={(e) => {
                              e.preventDefault();
                              const text = (e.clipboardData || (window as any).clipboardData).getData('text') || '';
                              setEditData((s: any) => ({ ...s, name: text }));
                            }}
                            placeholder="Enter your name"
                          />
                        </div>
                        <div className="col-md-6">
                          <label className="form-label">Email Id</label>
                          <input
                            className="form-control"
                            inputMode="email"
                            autoComplete="email"
                            value={editData.email || ""}
                            onChange={(e) => setEditData((s: any) => ({ ...s, email: e.target.value.trim() }))}
                            onBlur={(e) => {
                              const v = e.target.value.trim();
                              if (!EMAIL_RE.test(v)) {
                                toast.warn("Please enter a valid email address (e.g., name@example.com).");
                              }
                            }}
                            placeholder="name@example.com"
                          />
                        </div>

                        <div className="col-md-6">
                          <label className="form-label">Contact</label>
                          <div className="d-flex gap-2">
                            {/* Country code dropdown */}
                            <div style={{ minWidth: 180, width: 180 }}>
                              <CommonSelect
                                className="select "
                                options={DIAL_CODE_OPTIONS}
                                value={phoneDial}
                                onChange={(opt: any) => setPhoneDial(opt || DIAL_CODE_OPTIONS[0])}
                                forceFullWidth
                              />
                            </div>

                            {/* 10-digit local number */}
                            <input
                              className="form-control search-size"
                              type="tel"
                              inputMode="numeric"
                              autoComplete="tel-national"
                              placeholder="10-digit number"
                              maxLength={10}
                              value={phoneLocal}
                              onChange={(e) => {
                                const digits = e.target.value.replace(/\D/g, "").slice(0, 10);
                                setPhoneLocal(digits);
                              }}
                              onKeyDown={(e) => {
                                const k = e.key;
                                const nav = ["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Tab", "Home", "End"];
                                if (nav.includes(k)) return;
                                if (/^\d$/.test(k)) return;          // digits only
                                if (e.ctrlKey || e.metaKey) return;  // allow shortcuts
                                e.preventDefault();
                              }}
                              onPaste={(e) => {
                                e.preventDefault();
                                const text = (e.clipboardData || (window as any).clipboardData).getData("text") || "";
                                const digits = text.replace(/\D/g, "").slice(0, 10);
                                setPhoneLocal(digits);
                              }}
                            />
                          </div>
                          <small className="text-muted">
                            Saved as: <strong>{phoneDial.value} {phoneLocal || "__________"}</strong>
                          </small>
                        </div>


                        <div className="col-md-6">
                          <label className="form-label">Category</label>
                          <CommonSelect
                            className="select"
                            options={resumeCategories} // [{label,name},{value,id}]
                            value={resumeCategories.find(o => o.value === editData?.category_id) || null}
                            onChange={(opt: any) =>
                              setEditData((s: any) => ({
                                ...s,
                                category_id: opt?.value ?? "",
                                category_name: opt?.label ?? "",
                              }))
                            }
                            forceFullWidth={true}
                          />
                        </div>

                        {/* Total Experience (years & months like details view) */}
                        <div className="col-md-6">
                          <label className="form-label">Total Experience</label>
                          <div className="d-flex gap-2">
                            <input
                              type="number"
                              min={0}
                              className="form-control"
                              placeholder="Years"
                              value={editData?.exp_years ?? 0}
                              onChange={(e) => {
                                const y = Math.max(0, parseInt(e.target.value || "0", 10));
                                setEditData((s: any) => ({
                                  ...s,
                                  exp_years: y,
                                  total_experience_months: y * 12 + (s.exp_months || 0),
                                }));
                              }}
                            />
                            <input
                              type="number"
                              min={0}
                              max={11}
                              className="form-control"
                              placeholder="Months"
                              value={editData?.exp_months ?? 0}
                              onChange={(e) => {
                                let m = Math.max(0, parseInt(e.target.value || "0", 10));
                                if (m > 11) m = 11;
                                setEditData((s: any) => ({
                                  ...s,
                                  exp_months: m,
                                  total_experience_months: (s.exp_years || 0) * 12 + m,
                                }));
                              }}
                            />
                          </div>
                          <small className="text-muted d-block mt-1">
                            {fmtMonths(editData?.total_experience_months)}
                          </small>
                        </div>
                        {/* <div className="col-md-6">
                          <label className="form-label">Location</label>
                          <input className="form-control" value={editData.current_location || ""}
                            onChange={(e)=>setEditData((s:any)=>({...s,current_location:e.target.value}))}/>
                        </div> */}
                        <div className="col-md-6">
                          <label className="form-label">Designation</label>
                          <input
                            className="form-control"
                            value={editData.designation || ""}
                            onChange={(e) =>
                              setEditData((s: any) => ({
                                ...s,
                                // remove digits only — do NOT trim or collapse spaces while the user types
                                designation: String(e.target.value).replace(/\d+/g, "")
                              }))
                            }
                            onBeforeInput={(e: any) => {
                              // block digits only (allow spaces and other characters)
                              if (e?.data && /\d/.test(e.data)) e.preventDefault();
                            }}
                            onPaste={(e) => {
                              e.preventDefault();
                              const text = (e.clipboardData || (window as any).clipboardData).getData('text') || '';
                              // sanitize paste by removing digits but preserve spaces
                              setEditData((s: any) => ({ ...s, designation: text.replace(/\d+/g, "") }));
                            }}
                            placeholder="e.g., Software Engineer (no numbers)"
                          />
                        </div>

                        <div className="col-md-6">
                          <label className="form-label">Source</label>
                          <input
                            className="form-control"
                            value={editData.source || ""}
                            onChange={(e) =>
                              setEditData((s: any) => ({ ...s, source: sanitizeNoDigits(e.target.value) }))
                            }
                            onBeforeInput={(e: any) => { if (/\d/.test(e?.data || '')) e.preventDefault(); }}
                            onPaste={(e) => {
                              e.preventDefault();
                              const text = (e.clipboardData || (window as any).clipboardData).getData('text') || '';
                              setEditData((s: any) => ({ ...s, source: sanitizeNoDigits(text) }));
                            }}
                            placeholder="e.g., LinkedIn / Referral (no numbers)"
                          />
                        </div>

                        {/* CTC Info */}
                        <div className="col-md-6">
                          <label className="form-label">CTC Info</label>
                          <input
                            className="form-control"
                            placeholder="e.g., Current: 10 LPA, Expected: 12 LPA or Not Available"
                            value={editData?.ctc_info || ""}
                            onChange={(e) => {
                              const v = e.target.value;

                              //  Block junk characters while typing
                              if (!CTC_ALLOWED_CHARS.test(v)) return;

                              setEditData((s: any) => ({ ...s, ctc_info: v }));
                            }}

                            onPaste={(e) => {
                              e.preventDefault();
                              const text =
                                (e.clipboardData || (window as any).clipboardData)
                                  .getData("text")
                                  .trim();

                              if (!CTC_REGEX.test(text)) {
                                toast.error("Invalid CTC format.");
                                return;
                              }

                              if (!isCTCInRange(text)) {
                                toast.error("CTC must be between 0.5 LPA and 100 LPA.");
                                return;
                              }

                              setEditData((s: any) => ({ ...s, ctc_info: text }));
                            }}

                          />

                          <small className="text-muted d-block mt-1">
                            Allowed:
                            <strong> 10 LPA</strong>, <strong>10.5 LPA</strong>,
                            <strong> Not Available</strong>
                          </small>
                        </div>



                        {/* Loyalty (percent) */}
                        <div className="col-md-6">
                          <label className="form-label">Loyalty (%)</label>
                          <input
                            type="number"
                            min={0}
                            max={100}
                            className="form-control"
                            value={editData.loyalty_percentage ?? ""}
                            onChange={(e) =>
                              setEditData((s: any) => ({
                                ...s,
                                loyalty_percentage:
                                  e.target.value === ""
                                    ? ""
                                    : Math.max(0, Math.min(100, Number(e.target.value))),
                              }))
                            }
                          />
                        </div>

                        {/* Add skills input (sits next to Loyalty) */}
                        <div className="col-md-12">
                          <label className="form-label">Add Skills</label>
                          <div className="input-group">
                            <input
                              className="form-control"
                              placeholder="Type or paste, then press Enter"
                              value={skillInput}
                              onChange={(e) => setSkillInput(e.target.value)}
                              inputMode="text"
                              // hard block number keys
                              onKeyDown={(e) => {
                                const k = e.key;
                                const allowedNav = ["Backspace", "Delete", "ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown", "Home", "End", "Tab"];
                                if (/^[0-9]$/.test(k) && !e.ctrlKey && !e.metaKey && !e.altKey) {
                                  e.preventDefault();
                                  toast.dismiss(); // avoid stacking
                                  toast.warn("Numbers are not allowed in skills.");
                                  return;
                                }
                                if (k === "Enter" || k === ",") {
                                  e.preventDefault();
                                  addSkill();
                                }
                              }}
                              // block inserted numeric characters (covers IME/voice etc.)
                              onBeforeInput={(e: any) => {
                                const data = e?.data ?? "";
                                if (/\d/.test(data)) {
                                  e.preventDefault();
                                  toast.dismiss();
                                  toast.warn("Numbers are not allowed in skills.");
                                }
                              }}
                              // sanitize paste (strip digits)
                              onPaste={(e) => {
                                e.preventDefault();
                                const text = (e.clipboardData || (window as any).clipboardData).getData("text") || "";
                                const cleaned = text.replace(/\d+/g, ""); // drop all digits
                                setSkillInput((prev) => (prev + cleaned).replace(/\s+/g, " "));
                              }}
                              onBlur={() => {
                                if (skillInput.trim()) addSkill();
                              }}
                            />


                            <button type="button" className="btn btn-outline-secondary" onClick={() => addSkill()}>
                              Add
                            </button>
                          </div>
                          {/* <div className="form-text">Tip: paste “python, django, mysql” and press Enter.</div> */}
                        </div>

                        {/* Skills chips stay in their own row/section */}
                        <div className="col-12">
                          <label className="form-label">Skills</label>
                          <div className="d-flex flex-wrap gap-2">
                            {(editData?.skills || []).map((s: string, i: number) => (
                              <span
                                key={i}
                                className="badge bg-light text-dark border d-inline-flex align-items-center px-2 py-1"
                              >
                                <span className="me-1">{s}</span>
                                <button
                                  type="button"
                                  className="btn btn-sm p-0 ms-1 text-secondary"
                                  aria-label="Remove"
                                  title="Remove"
                                  onClick={() => removeSkill(i)}
                                  style={{
                                    lineHeight: 1,
                                    border: "none",
                                    background: "transparent",
                                    display: "inline-flex",
                                    alignItems: "center",
                                  }}
                                >
                                  <i className="ti ti-x" />
                                </button>
                              </span>
                            ))}
                            {(!editData?.skills || editData.skills.length === 0) && (
                              <span className="text-muted small">—</span>
                            )}
                          </div>
                        </div>


                      </form>
                    )}
                  </div>
                  <div className="modal-footer border-0">
                    <button className="btn btn-cancel me-1" onClick={() => hideBsModal("resume_edit_modal")}>Cancel</button>
                    <button className="btn btn-primary" disabled={editSaving || !editData} onClick={handleSaveEdit}>
                      {editSaving ? "Saving…" : "Save"}
                    </button>
                  </div>
                </div>
              </div>
            </div>
            {/* ======== NEW: Career Modal with Tabs ======== */}
            <div className={`modal fade ${showCareerModal ? "show" : ""}`} id="career_modal" style={{ display: showCareerModal ? "block" : "none" }} aria-modal={showCareerModal} role="dialog">
              <div className="modal-dialog modal-lg modal-dialog-centered">
                <div className="modal-content border-0 shadow-lg">
                  <div className="modal-header border-0">
                    <h5 className="modal-title">Careers</h5>
                    <button
                      type="button"
                      className="btn-close"
                      onClick={() => {
                        resetCareerModalState();
                        setShowCareerModal(false);
                        // clean up if you want
                        // setSelectedFiles([]); setUploadResp(null); setGenLinkData(null);
                      }}
                    />
                  </div>

                  <div className="modal-body pt-0" style={{ display: "flex", flexDirection: "column", minHeight: "350px" }}>
                    {/* Tabs header */}
                    <ul className="nav nav-tabs mt-2">
                      <li className="nav-item">
                        <button
                          className={`nav-link ${careerActiveTab === "generate" ? "active" : ""}`}
                          onClick={() => {
                            // clear Upload tab state when leaving it
                            setSelectedFiles([]);
                            setUploadResp(null);
                            setCareerActiveTab("generate");
                            if (!genLinkData) void handleGenerateLink();
                          }}
                        >
                          Generate Link
                        </button>
                      </li>
                      <li className="nav-item">
                        <button
                          className={`nav-link ${careerActiveTab === "upload" ? "active" : ""}`}
                          onClick={() => {  // clear Upload tab state when leaving it
                            setSelectedFiles([]);
                            setUploadResp(null);
                            setCareerActiveTab("upload")
                          }}
                        >
                          Upload Resumes
                        </button>
                      </li>
                    </ul>

                    {/* Tabs content */}
                    <div className="tab-content p-3 border border-top-0 rounded-bottom" style={{ flex: 1, width: "100%" }}>
                      {/* Generate Link tab */}
                      <div className={`tab-pane fade ${careerActiveTab === "generate" ? "show active" : ""}`} style={{ width: "100%" }}       >
                        <div className="d-flex align-items-center gap-2 mb-3">
                          <button className="btn btn-outline-primary" onClick={handleGenerateLink} disabled={genLinkLoading}>
                            {genLinkLoading ? "Generating…" : "Generate New Link"}
                          </button>
                        </div>

                        {genLinkLoading && (
                          <div style={{ minHeight: "200px", display: "flex", alignItems: "center", justifyContent: "center" }}>
                            <span className="spinner-border text-primary" role="status"></span>
                            <span className="ms-2">Fetching link…</span>
                          </div>
                        )}

                        {!genLinkLoading && genLinkData && (
                          <div className="card border-0 shadow-sm">
                            <div className="card-body">
                              <div className="mb-2">
                                <div className="text-muted small">Link</div>
                                <div className="d-flex align-items-center gap-2">
                                  <a href={genLinkData.link} target="_blank" rel="noreferrer" className="text-break">
                                    {genLinkData.link || "—"}
                                  </a>
                                  {genLinkData.link && (
                                    <button
                                      className="btn btn-sm btn-outline-secondary"
                                      onClick={async () => {
                                        try {
                                          await navigator.clipboard.writeText(genLinkData.link!);
                                          toast.success("Link copied");
                                        } catch {
                                          toast.error("Failed to copy");
                                        }
                                      }}
                                      title="Copy"
                                    >
                                      <i className="ti ti-copy" />
                                    </button>
                                  )}
                                </div>
                              </div>
                              <div>
                                <div className="text-muted small">Valid Until</div>
                                <div>
                                  {genLinkData.expires_at
                                    ? dayjs(genLinkData.expires_at).format("DD-MMM-YYYY HH:mm")
                                    : "—"}
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Upload tab */}
                      <div className={`tab-pane fade ${careerActiveTab === "upload" ? "show active" : ""}`} style={{ width: "100%" }}      >

                        <div className="mb-3">
                          <label className="form-label">Select up to {MAX_FILES} files</label>

                          <div className="input-group">
                            <input
                              type="file"
                              id="resumeFileInput"
                              className="form-control d-none"   // hide the ugly native label
                              accept={ALLOWED_EXTS.join(",")}
                              multiple
                              onChange={onPickFiles}
                            />
                            <label
                              htmlFor="resumeFileInput"
                              className="form-control text-muted"
                              style={{ cursor: "pointer" }}
                            >
                              {selectedFiles.length} / {MAX_FILES} files selected
                            </label>
                          </div>

                          <div className="form-text">
                            Allowed: {ALLOWED_EXTS.join(", ").toUpperCase()}
                          </div>
                        </div>


                        {selectedFiles.length > 0 && (
                          <div className="mb-3">
                            <div className="d-flex flex-wrap gap-2">
                              {selectedFiles.map((f, i) => (
                                <span key={i} className="badge bg-light text-dark border d-inline-flex align-items-center px-2 py-1">
                                  <span className="me-2">{f.name}</span>
                                  <button
                                    type="button"
                                    className="btn btn-sm p-0 ms-1 text-secondary"
                                    onClick={() => removePickedFile(i)}
                                    title="Remove"
                                    style={{ lineHeight: 1, border: "none", background: "transparent" }}
                                  >
                                    <i className="ti ti-x" />
                                  </button>
                                </span>
                              ))}
                            </div>
                          </div>
                        )}

                        <div className="d-flex gap-2 mb-3">
                          <button className="btn btn-outline-primary" onClick={handleUpload} disabled={uploading || selectedFiles.length === 0} title="Upload resumes">
                            {uploading ? "Uploading…" : "Upload"}
                          </button>
                          <button
                            className="btn btn-primary"
                            onClick={handleSaveUpload}
                            disabled={savingUploaded || !uploadResp?.resumes?.length}
                            title={!uploadResp?.resumes?.length ? "Upload first" : "Save resumes"}
                          >
                            {savingUploaded ? "Saving…" : "Save"}
                          </button>
                        </div>

                        {/* Preview parsed resumes (from /upload response) */}
                        {uploadResp?.resumes?.length ? (
                          <div className="card border-0 shadow-sm">
                            <div className="card-body">
                              <h6 className="text-uppercase text-muted mb-3">Parsed Resumes</h6>
                              <div className="table-responsive">
                                <table className="table table-sm align-middle">
                                  <thead>
                                    <tr>
                                      <th>Original File</th>
                                      <th>Name</th>
                                      <th>Email Id</th>
                                      <th>Contact No</th>
                                      <th>Designation</th>
                                      <th>Category</th>
                                      <th>Total Experience</th>
                                      <th>Source</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {uploadResp.resumes.map((r: any, idx: number) => (
                                      <tr key={idx}>
                                        <td>{r?.original_filename || "—"}</td>
                                        <td>{r?.Name || "—"}</td>
                                        <td>{r?.["Email Id"] || "—"}</td>
                                        <td>{r?.["Contact No"] || "—"}</td>
                                        <td>{r?.Designation || "—"}</td>
                                        <td>{r?.Category || "—"}</td>
                                        <td>{r?.["Total Experience"] ?? "—"}</td>
                                        <td>{r?.Source || "—"}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                              <div className="text-muted small mt-2">
                                File references: {Object.keys(uploadResp.file_references || {}).length}
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div className="text-muted small">
                            No parsed data yet. Choose files and Upload.
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="modal-footer border-0">
                    <button className="btn btn-primary" onClick={() => { resetCareerModalState(); setShowCareerModal(false) }}>Close</button>
                  </div>
                </div>
              </div>
            </div>

            {/* Backdrop for manual show/hide */}
            {showCareerModal && <div className="modal-backdrop fade show" />}

          </div>
        </div>
        <Footer />
      </div>
      <ToastContainer position="top-right" autoClose={3000} />
    </>
  );
};

export default Resumes;